import { useState } from "react";
import { Link } from "react-router-dom";
import { MessagesSquare, Search, RotateCcw, UserRound } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { useApi } from "../../lib/useApi";
import { Loader, ErrorState, EmptyState, PageHeader } from "../../components/common/States";
import { StatusBadge, TypeBadge, PriorityBadge, Stars } from "../../components/common/Badges";
import FeedbackStats from "../../components/company/FeedbackStats";
import { STATUS, PRIORITY, CATEGORY, CHANNEL, fmtDate } from "../../lib/labels";

const EMPTY = { date_from: "", date_to: "", agency_id: "", service_id: "", rating: "", category: "", status: "", priority: "",
  channel: "", assigned_to: "", q: "" };
const SORTS = [["recent", "Plus récents"], ["oldest", "Plus anciens"], ["priority", "Priorité"], ["rating_asc", "Note croissante"],
  ["rating_desc", "Note décroissante"], ["updated", "Dernière activité"]];

const Filter = ({ value, onChange, options, all, testId }) => (
  <select className="field-select" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
    {all && <option value="">{all}</option>}
    {options.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
  </select>
);

const opts = (m) => Object.entries(m).map(([k, v]) => [k, typeof v === "string" ? v : v.label]);
const qsOf = (o) => new URLSearchParams(Object.entries(o).filter(([, v]) => v !== "")).toString();

const Row = ({ f }) => (
  <Link to={`/app/feedbacks/${f.id}`} data-testid={`feedback-row-${f.id}`} className="block px-5 py-4 transition-colors hover:bg-slate-50">
    <div className="flex flex-wrap items-center gap-2">
      <span className="font-mono text-xs text-slate-500">{f.tracking_code}</span>
      <TypeBadge value={f.type} /><StatusBadge value={f.status} /><PriorityBadge value={f.priority} />
      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{CHANNEL[f.channel] || f.channel}</span>
      {f.abuse_status === "reported" && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-800">Signalé</span>}
      {f.sensitive && <span className="rounded-full bg-rose-600 px-2 py-0.5 text-xs text-white" data-testid={`row-sensitive-${f.id}`}>Sensible</span>}
      <span className="ml-auto text-xs text-slate-400">{fmtDate(f.created_at)}</span>
    </div>
    {f.title && <p className="mt-2 text-sm font-semibold text-slate-900">{f.title}</p>}
    <p className="mt-1 line-clamp-2 text-sm text-slate-700">{f.message}</p>
    <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500">
      <Stars value={f.rating} /><span>{f.anonymous ? "Anonyme" : f.contact_name}</span>{f.category && <span>· {CATEGORY[f.category]}</span>}
      <span className="flex items-center gap-1"><UserRound className="h-3 w-3" />{f.assignee_name || "Non affecté"}</span>
    </div>
  </Link>
);

const Filters = ({ filters, set, agencies, services, team, onReset }) => (
  <div className="card-surface mb-4 grid gap-2 p-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6" data-testid="feedbacks-filters">
    <label className="text-xs text-slate-500">Du<Input type="date" value={filters.date_from} onChange={(e) => set("date_from")(e.target.value)} data-testid="filter-date-from" /></label>
    <label className="text-xs text-slate-500">Au<Input type="date" value={filters.date_to} onChange={(e) => set("date_to")(e.target.value)} data-testid="filter-date-to" /></label>
    <Filter testId="filter-agency" value={filters.agency_id} onChange={set("agency_id")} all="Toutes agences" options={(agencies || []).map((a) => [a.id, a.name])} />
    <Filter testId="filter-service" value={filters.service_id} onChange={set("service_id")} all="Tous services"
      options={(services || []).filter((s) => !filters.agency_id || s.agency_id === filters.agency_id).map((s) => [s.id, s.name])} />
    <Filter testId="filter-rating" value={filters.rating} onChange={set("rating")} all="Toutes notes" options={[5, 4, 3, 2, 1].map((n) => [String(n), `${n} étoile(s)`])} />
    <Filter testId="filter-category" value={filters.category} onChange={set("category")} all="Toutes catégories" options={opts(CATEGORY)} />
    <Filter testId="filter-status" value={filters.status} onChange={set("status")} all="Tous statuts" options={opts(STATUS)} />
    <Filter testId="filter-priority" value={filters.priority} onChange={set("priority")} all="Toutes priorités" options={opts(PRIORITY)} />
    <Filter testId="filter-channel" value={filters.channel} onChange={set("channel")} all="Tous canaux" options={opts(CHANNEL)} />
    <Filter testId="filter-assigned" value={filters.assigned_to} onChange={set("assigned_to")} all="Toutes affectations"
      options={[["me", "Affectés à moi"], ["none", "Non affectés"], ...(team || []).map((m) => [m.user_id, m.name])]} />
    <Button variant="ghost" className="self-end" onClick={onReset} data-testid="filters-reset"><RotateCcw className="mr-1.5 h-4 w-4" />Réinitialiser</Button>
  </div>
);

export default function Feedbacks() {
  const [filters, setFilters] = useState(EMPTY);
  const [view, setView] = useState({ sort: "recent", page: 1, page_size: 20 });
  const [q, setQ] = useState("");
  const agencies = useApi("/company/agencies");
  const services = useApi("/company/services");
  const team = useApi("/company/team");
  const fq = qsOf(filters);
  const { data, loading, error, reload } = useApi(`/company/feedbacks?${qsOf({ ...filters, ...view })}`);
  const stats = useApi(`/company/feedbacks/stats?${fq}`);
  const set = (k) => (v) => { setFilters((f) => ({ ...f, [k]: v, ...(k === "agency_id" ? { service_id: "" } : {}) })); setView((s) => ({ ...s, page: 1 })); };
  const reset = () => { setFilters(EMPTY); setQ(""); setView((s) => ({ ...s, page: 1 })); };

  return (
    <div>
      <PageHeader title="Feedbacks" description="Avis, suggestions et réclamations reçus par votre organisation, tous canaux confondus." />
      <FeedbackStats stats={stats.data} />
      <div className="mb-3 flex flex-col gap-2 sm:flex-row">
        <form className="relative flex-1" onSubmit={(e) => { e.preventDefault(); set("q")(q); }}>
          <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher (message, titre, référence, nom)… puis Entrée" className="h-10 pl-9" data-testid="feedbacks-search-input" />
        </form>
        <div className="sm:w-52"><Filter testId="feedbacks-sort" value={view.sort} onChange={(v) => setView((s) => ({ ...s, sort: v, page: 1 }))} options={SORTS} /></div>
      </div>
      <Filters filters={filters} set={set} agencies={agencies.data} services={services.data} team={team.data} onReset={reset} />
      {loading && !data && <Loader />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {data && !data.items.length && <EmptyState icon={MessagesSquare} title="Aucun feedback" text="Aucun résultat pour ces critères." />}
      {data && data.items.length > 0 && (
        <>
          <div className={`card-surface divide-y overflow-hidden transition-opacity ${loading ? "opacity-60" : ""}`} data-testid="feedbacks-list">
            {data.items.map((f) => <Row key={f.id} f={f} />)}
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-500">
            <span data-testid="feedbacks-total">{data.total} feedback(s)</span>
            <div className="flex items-center gap-2">
              <select className="field-select w-28" value={view.page_size} onChange={(e) => setView((s) => ({ ...s, page_size: Number(e.target.value), page: 1 }))} data-testid="feedbacks-page-size">
                {[10, 20, 50].map((n) => <option key={n} value={n}>{`${n} / page`}</option>)}
              </select>
              <Button variant="outline" size="sm" disabled={data.page <= 1} onClick={() => setView((s) => ({ ...s, page: s.page - 1 }))} data-testid="feedbacks-prev-page">Précédent</Button>
              <span data-testid="feedbacks-page-indicator">{data.page} / {data.pages}</span>
              <Button variant="outline" size="sm" disabled={data.page >= data.pages} onClick={() => setView((s) => ({ ...s, page: s.page + 1 }))} data-testid="feedbacks-next-page">Suivant</Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
